import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { useTheme } from '../shell/theme.js';
import { AdminLogo } from '../shell/admin-logo.js';
import { Banner, Button, Card, CardBody, Field, Icon, Input } from '../ui/index.js';
import '../styles/login.mobile.css';

const ERROR_LABEL: Record<string, string> = {
  username_and_password_required: 'Kullanıcı adı ve şifre zorunludur.',
  invalid_credentials: 'Kullanıcı adı veya şifre hatalı.',
  account_disabled: 'Hesabınız devre dışı bırakılmış. DB üzerinden admin_users.status alanını kontrol edin.',
  account_disabled_by_admin: 'Hesabınız bir yönetici tarafından devre dışı bırakılmış.',
  network_error: 'API sunucusuna ulaşılamadı.',
};

function describeError(reason: string | null): string {
  if (!reason) return '';
  return ERROR_LABEL[reason] ?? `Giriş başarısız (${reason}).`;
}

export function LoginPage(): JSX.Element {
  const session = useSession();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const from = search.get('from') ?? '/';
  const reason = search.get('reason');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session.state.kind === 'cookie_session') {
    return <Navigate to={from} replace />;
  }

  if (session.state.kind === 'loading') {
    return (
      <LoginShellChrome theme={theme}>
        <Card>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 650 }}>Kopechka Yönetim</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Oturum doğrulanıyor…</p>
        </Card>
      </LoginShellChrome>
    );
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('username_and_password_required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await session.signInWithPassword(username.trim(), password);
      if (result.kind === 'ok') {
        navigate(from, { replace: true });
        return;
      }
      if (result.kind === 'invalid_credentials') {
        setError('invalid_credentials');
      } else if (result.kind === 'account_disabled') {
        setError(result.reason);
      } else {
        setError(result.reason);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <LoginShellChrome theme={theme}>
      <div className="adm-login-hero">
        <span className="role-pill adm-login-pill">
          <Icon.Lock size={11} />
          Operatör Konsolu
        </span>
        <h1 className="adm-login-title">Yönetim panelinde oturum aç</h1>
        <p className="t-muted adm-login-intro">
          Yetkilendirilmiş operatör hesabıyla giriş yapın. Tüm aksiyonlar audit'e yazılır.
        </p>
      </div>

      {reason === 'expired' ? (
        <div className="adm-login-banner-slot">
          <Banner tone="warn">Oturumun süresi doldu. Lütfen yeniden giriş yap.</Banner>
        </div>
      ) : reason === 'unauthenticated' ? (
        <div className="adm-login-banner-slot">
          <Banner tone="warn">Bu sayfaya erişmek için giriş yapmalısın.</Banner>
        </div>
      ) : null}

      <Card flush className="adm-login-card">
        <CardBody className="adm-login-body">
          <form onSubmit={onSubmit} aria-label="yönetim girişi formu">
            <div style={{ marginBottom: 14 }}>
              <Field label="Operatör kullanıcı adı" htmlFor="username">
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  disabled={submitting}
                  placeholder="ör. mert.y"
                  required
                />
              </Field>
            </div>

            <div style={{ marginBottom: 18 }}>
              <Field label="Şifre" htmlFor="password">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={submitting}
                  placeholder="••••••••"
                  required
                  iconRight={
                    <button
                      type="button"
                      className="icon-right"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'şifreyi gizle' : 'şifreyi göster'}
                      tabIndex={-1}
                    >
                      {showPassword ? <Icon.EyeOff size={14} /> : <Icon.Eye size={14} />}
                    </button>
                  }
                />
              </Field>
            </div>

            <Button
              type="submit"
              variant="primary"
              block
              className="adm-login-submit"
              loading={submitting}
              iconRight={!submitting ? <Icon.ArrowUR size={14} /> : undefined}
            >
              {submitting ? 'Oturum açılıyor…' : 'Oturum aç'}
            </Button>

            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: 'var(--bg-sunken)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <Icon.Info size={14} style={{ color: 'var(--brand-600)', marginTop: 1, flexShrink: 0 }} />
              <div className="t-xs t-tertiary" style={{ lineHeight: 1.5 }}>
                Rol; sunucudaki <code style={{ fontFamily: 'var(--font-mono)' }}>admin_users.role</code> alanından
                gelir ve oturum kapasitelerini belirler. Bu form rolü değiştirmez — yalnızca kimlik doğrular.
              </div>
            </div>
          </form>

          {error ? (
            <div className="adm-login-error-slot">
              <Banner tone="danger" testId="login-error">
                {describeError(error)}
              </Banner>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div className="adm-login-help">
        Yardıma mı ihtiyacın var?{' '}
        <span className="adm-login-help-addr">operator-help@kopechka</span>
      </div>
    </LoginShellChrome>
  );
}

function LoginShellChrome({
  theme,
  children,
}: {
  readonly theme: ReturnType<typeof useTheme>;
  readonly children: React.ReactNode;
}): JSX.Element {
  const themeIsDark = theme.theme === 'dark';
  return (
    <div className="adm-login">
      <div className="adm-login-head">
        <AdminLogo />
        <button
          type="button"
          className="iconbtn"
          onClick={theme.toggleTheme}
          aria-label={themeIsDark ? 'açık temaya geç' : 'koyu temaya geç'}
          aria-pressed={themeIsDark}
          title={themeIsDark ? 'Açık temaya geç' : 'Koyu temaya geç'}
          data-testid="login-theme-toggle"
        >
          {themeIsDark ? <Icon.Sun /> : <Icon.Moon />}
        </button>
      </div>
      <div className="adm-login-main">
        <div className="adm-login-col">{children}</div>
      </div>
    </div>
  );
}
